import { useState } from "react";
import { Armchair, UtensilsCrossed, Luggage, Check, Leaf, ScrollText } from "lucide-react";
import { SeatMap } from "./SeatMap";

const card = "bg-white rounded-2xl border border-slate-100 p-5 sm:p-6";
const H = ({ icon: Icon, children, right }) => (
  <div className="flex items-center justify-between mb-4">
    <h2 className="font-heading text-lg font-bold flex items-center gap-2"><Icon className="h-5 w-5 text-[#EC5B24]" /> {children}</h2>{right}
  </div>
);

export const addonTotals = (data, v) => {
  if (!data) return { seats: 0, meals: 0, baggage: 0 };
  let seats = 0, meals = 0;
  data.seat_maps.forEach((m) => {
    const price = {}; m.rows.forEach((r) => r.seats.forEach((s) => (price[s.id] = s.price)));
    (v.seats[m.slice] || []).forEach((id) => (seats += price[id] || 0));
    const menu = Object.fromEntries(m.meal_menu.items.map((x) => [x.id, x.price]));
    (v.meals[m.slice] || []).forEach((id) => (meals += id ? menu[id] || 0 : 0));
  });
  const bag = Object.fromEntries(data.baggage_options.map((o) => [o.kg, o.price]));
  const baggage = v.baggage.reduce((a, kg) => a + (kg ? bag[kg] || 0 : 0), 0);
  return { seats, meals, baggage };
};

const SliceTabs = ({ maps, active, setActive, testid }) => maps.length > 1 && (
  <div className="flex gap-2 mb-4">
    {maps.map((m) => (
      <button key={m.slice} data-testid={`${testid}-tab-${m.slice}`} onClick={() => setActive(m.slice)}
        className={`rounded-full px-4 py-1.5 text-sm font-semibold ${active === m.slice ? "bg-[#1C1A4A] text-white" : "bg-slate-100 text-slate-600"}`}>
        {m.label} · {m.flight_no}
      </button>
    ))}
  </div>
);

export const SeatSection = ({ data, value, onChange, seatPax, money }) => {
  const [active, setActive] = useState(0);
  const map = data.seat_maps[active];
  const sel = value.seats[active] || [];
  return (
    <section className={card} data-testid="seat-section">
      <H icon={Armchair} right={<span className="text-xs text-slate-500" data-testid="seats-selected-count">{sel.length}/{seatPax} selected</span>}>Choose your seats</H>
      <SliceTabs maps={data.seat_maps} active={active} setActive={setActive} testid="seat-slice" />
      <p className="text-xs text-slate-500 mb-4">{map.airline} · {map.flight_no} · {map.airplane || "Aircraft"} layout. Seats are requested with the airline when we issue your ticket; if a seat is taken, we refund its price.</p>
      <SeatMap map={map} selected={sel} max={seatPax} money={money}
        onToggle={(s) => onChange({ ...value, seats: { ...value.seats, [active]: sel.includes(s.id) ? sel.filter((x) => x !== s.id) : [...sel, s.id] } })} />
    </section>
  );
};

export const MealSection = ({ data, value, onChange, travellers, money }) => {
  const [active, setActive] = useState(0);
  const map = data.seat_maps[active];
  const menu = map.meal_menu;
  const picks = value.meals[active] || [];
  const set = (ti, id) => {
    const next = [...picks]; next[ti] = next[ti] === id ? null : id;
    onChange({ ...value, meals: { ...value.meals, [active]: next } });
  };
  return (
    <section className={card} data-testid="meal-section">
      <H icon={UtensilsCrossed}>Meals on board</H>
      <SliceTabs maps={data.seat_maps} active={active} setActive={setActive} testid="meal-slice" />
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <span className="font-semibold text-slate-900" data-testid="meal-brand">{menu.brand}</span>
        {menu.complimentary && <span className="text-[10px] font-bold uppercase bg-[#E8F5E9] text-[#0F9D58] rounded-full px-2 py-0.5">Complimentary — pick a preference</span>}
      </div>
      {travellers.map((t, ti) => t.type !== "infant" && (
        <div key={ti} className="mb-5 last:mb-0">
          <div className="text-xs font-semibold text-slate-500 mb-2">{t.first_name ? `${t.first_name} ${t.last_name}` : `Traveller ${ti + 1}`}</div>
          <div className="flex gap-3 overflow-x-auto pb-2 snap-x">
            {menu.items.map((m) => {
              const on = picks[ti] === m.id;
              return (
                <button key={m.id} data-testid={`meal-${active}-${ti}-${m.id}`} onClick={() => set(ti, m.id)}
                  className={`snap-start shrink-0 w-44 text-left rounded-xl border-2 overflow-hidden transition-colors ${on ? "border-[#EC5B24]" : "border-slate-100 hover:border-slate-300"}`}>
                  <div className="relative h-24">
                    <img src={m.image} alt={m.name} className="h-full w-full object-cover" loading="lazy" />
                    {on && <span className="absolute top-1.5 right-1.5 h-6 w-6 rounded-full bg-[#EC5B24] text-white grid place-items-center"><Check className="h-3.5 w-3.5" /></span>}
                    <span className={`absolute bottom-1.5 left-1.5 h-4 w-4 rounded-sm border-2 bg-white grid place-items-center ${m.veg ? "border-green-600" : "border-red-600"}`}><span className={`h-1.5 w-1.5 rounded-full ${m.veg ? "bg-green-600" : "bg-red-600"}`} /></span>
                  </div>
                  <div className="p-2.5">
                    <div className="text-sm font-semibold leading-tight">{m.name}</div>
                    <div className="text-[11px] text-slate-500 leading-snug mt-0.5 line-clamp-2">{m.desc}</div>
                    <div className="font-mono text-sm font-bold mt-1">{m.price ? money(m.price) : <span className="text-[#0F9D58]">Included</span>}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ))}
      <p className="text-[11px] text-slate-500 flex items-center gap-1"><Leaf className="h-3 w-3" /> Menu as served by the airline; items may vary by route and season.</p>
    </section>
  );
};

export const BaggageSection = ({ data, value, onChange, travellers, money }) => (
  <section className={card} data-testid="baggage-section">
    <H icon={Luggage}>Baggage</H>
    <div className="grid sm:grid-cols-3 gap-3 text-sm mb-5" data-testid="baggage-policy">
      <div className="rounded-xl bg-slate-50 p-3"><div className="text-xs text-slate-500">Cabin</div><div className="font-semibold">{data.baggage_policy.cabin}</div></div>
      <div className="rounded-xl bg-slate-50 p-3"><div className="text-xs text-slate-500">Check-in (included)</div><div className="font-semibold">{data.baggage_policy.checkin}</div></div>
      <div className="rounded-xl bg-slate-50 p-3"><div className="text-xs text-slate-500">Infants</div><div className="font-semibold">{data.baggage_policy.infant.replace("Infants: ", "")}</div></div>
    </div>
    <div className="text-sm font-semibold mb-2">Add extra check-in baggage</div>
    {travellers.map((t, ti) => t.type !== "infant" && (
      <div key={ti} className="flex flex-wrap items-center gap-2 mb-3">
        <span className="text-xs text-slate-500 w-32 truncate">{t.first_name || `Traveller ${ti + 1}`}</span>
        {[{ kg: 0, price: 0 }, ...data.baggage_options].map((o) => {
          const on = (value.baggage[ti] || 0) === o.kg;
          return (
            <button key={o.kg} data-testid={`baggage-${ti}-${o.kg}`} onClick={() => { const b = [...value.baggage]; b[ti] = o.kg; onChange({ ...value, baggage: b }); }}
              className={`rounded-xl border px-3 py-1.5 text-xs font-semibold transition-colors ${on ? "border-[#EC5B24] bg-orange-50 text-[#EC5B24]" : "border-slate-200 hover:border-slate-300"}`}>
              {o.kg ? `+${o.kg} kg · ${money(o.price)}` : "None"}
            </button>
          );
        })}
      </div>
    ))}
    <p className="text-[11px] text-slate-500">{data.baggage_policy.note}</p>
  </section>
);

export const PolicySection = ({ cancelFee, assured, pax, money }) => (
  <section className={card} data-testid="cancellation-policy">
    <H icon={ScrollText}>Cancellation policy</H>
    {assured && <div className="mb-3 rounded-xl bg-[#E8F5E9] text-[#0F9D58] text-sm font-semibold px-3 py-2" data-testid="policy-full-refund">Full refund protection added — you'll get a full refund if you cancel.</div>}
    <ul className={`text-sm text-slate-600 space-y-1.5 list-disc pl-5 ${assured ? "opacity-60" : ""}`}>
      <li>Without protection, cancellation charge: <b className="text-slate-900" data-testid="cancel-fee-text">{money(cancelFee)} per traveller</b>{pax > 1 ? ` (${money(cancelFee * pax)} for ${pax} travellers)` : ""}.</li>
      <li>Airline cancellation fees, if any, are deducted as per the airline's fare rules.</li>
      <li>Add <b className="text-slate-900">Full refund protection</b> above to cancel with a full refund.</li>
      <li>Request cancellation anytime from My Trips. Refunds reach your original UPI account within 5–7 working days.</li>
      <li>No refund for no-shows or cancellations within 3 hours of departure.</li>
    </ul>
  </section>
);
